import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api";
import { CopyButton } from "../../components/CopyButton";

type U = { id: string; email: string; name: string; role: "admin" | "supervisor" | "rep"; active: boolean; enrolled: boolean; locked: boolean };
type Entry = { id: string; at: string; user_id: string | null; action: string; entity_type: string | null; entity_id: string | null };

export function Users() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: () => api<{ users: U[] }>("/api/admin/users") });
  const audit = useQuery({ queryKey: ["audit"], queryFn: () => api<{ entries: Entry[] }>("/api/admin/audit") });
  const [form, setForm] = useState({ email: "", name: "", role: "rep" });
  const [secret, setSecret] = useState<{ who: string; password: string } | null>(null);
  const [error, setError] = useState("");
  const done = () => { qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["audit"] }); };
  const act = useMutation({
    mutationFn: async (fn: () => Promise<void>) => { setError(""); await fn(); },
    onSuccess: done, onError: (e) => setError((e as Error).message),
  });
  const nameOf = (id: string | null) => users.data?.users.find((u) => u.id === id)?.name ?? (id ? "system" : "—");

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <h1 className="text-xl font-semibold">Users</h1>
      {secret && <div role="status" className="card border-amber-300 bg-amber-50 text-sm dark:bg-slate-900">
        One-time password for <b>{secret.who}</b> (shown once, share it privately): <code className="mx-1">{secret.password}</code>
        <CopyButton text={secret.password} /> <button className="btn-ghost text-xs" onClick={() => setSecret(null)}>Done</button>
      </div>}
      {(error || users.error || audit.error) && <p role="alert" className="text-sm text-red-600">{error || ((users.error ?? audit.error) as Error).message}</p>}
      <form className="card grid gap-2 sm:grid-cols-4" onSubmit={(e) => { e.preventDefault(); act.mutate(async () => {
        const r = await api<{ tempPassword: string }>("/api/admin/users", { method: "POST", body: form });
        setSecret({ who: form.email, password: r.tempPassword }); setForm({ email: "", name: "", role: "rep" });
      }); }}>
        <input className="input" type="email" required placeholder="Email" aria-label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input className="input" required placeholder="Full name" aria-label="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <select className="input" aria-label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="rep">Rep</option><option value="supervisor">Supervisor</option><option value="admin">Admin</option>
        </select>
        <button className="btn-primary justify-center">Add user</button>
      </form>
      <table className="card w-full text-left text-sm">
        <thead className="text-xs uppercase text-slate-500"><tr><th className="py-2">Name</th><th>Email</th><th>Role</th><th>2FA</th><th>Status</th><th></th></tr></thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">{users.data?.users.map((u) => (
          <tr key={u.id}>
            <td className="py-2">{u.name}</td><td>{u.email}</td>
            <td><select className="input py-0.5" aria-label={`Role for ${u.name}`} value={u.role}
              onChange={(e) => { const role = e.target.value; if (window.confirm(`Change ${u.name}'s role to ${role}?`)) act.mutate(() => api(`/api/admin/users/${u.id}`, { method: "PATCH", body: { role } })); }}>
              <option value="rep">Rep</option><option value="supervisor">Supervisor</option><option value="admin">Admin</option></select></td>
            <td>{u.enrolled ? "Set up" : "Not yet"}</td>
            <td>{!u.active ? "Deactivated" : u.locked ? "Locked" : "Active"}</td>
            <td className="flex flex-wrap gap-1 py-2">
              <button className="btn-ghost text-xs" onClick={() => { if (u.active && !window.confirm(`Deactivate ${u.name}? They will be signed out.`)) return; act.mutate(() => api(`/api/admin/users/${u.id}`, { method: "PATCH", body: { active: !u.active } })); }}>{u.active ? "Deactivate" : "Reactivate"}</button>
              <button className="btn-ghost text-xs" onClick={() => { if (window.confirm(`Reset 2FA for ${u.name}? They will set it up again at next sign-in.`)) act.mutate(() => api(`/api/admin/users/${u.id}/reset-2fa`, { method: "POST" })); }}>Reset 2FA</button>
              <button className="btn-ghost text-xs" onClick={() => act.mutate(async () => {
                const r = await api<{ tempPassword: string }>(`/api/admin/users/${u.id}/reset-password`, { method: "POST" });
                setSecret({ who: u.email, password: r.tempPassword });
              })}>Reset password</button>
            </td>
          </tr>
        ))}</tbody>
      </table>
      <section className="card"><h2 className="mb-2 text-sm font-semibold">Recent activity</h2>
        <ul className="max-h-96 overflow-auto text-xs">{audit.data?.entries.map((e) => (
          <li key={e.id} className="flex gap-3 py-0.5"><span className="w-40 shrink-0 text-slate-500">{new Date(e.at).toLocaleString()}</span>
            <span className="w-32 shrink-0">{nameOf(e.user_id)}</span><span>{e.action}{e.entity_type && ` · ${e.entity_type}`}</span></li>
        ))}</ul>
      </section>
    </div>
  );
}
